import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import { downloadPhotosAsZip } from '../services/exportService'
import { Button, Card } from '../components/common'
import { PhotoGallery } from '../components/survey'
import useSurveyStore from '../stores/surveyStore'
import useCategoryStore from '../stores/categoryStore'
import useMobileLayout from '../hooks/useMobileLayout'
import MobileSurveyPage from '../components/mobile/MobileSurveyPage'

export default function SurveyPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isMobile = useMobileLayout()
  const [downloadingPhotos, setDownloadingPhotos] = useState(false)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const loading = useSurveyStore(state => state.loading)
  const error = useSurveyStore(state => state.error)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const deletePhoto = useSurveyStore(state => state.deletePhoto)
  const updatePhotoNotes = useSurveyStore(state => state.updatePhotoNotes)
  const togglePhotoAnalyse = useSurveyStore(state => state.togglePhotoAnalyse)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)

  const categories = useCategoryStore(state => state.getActiveCategories())
  const loadCategories = useCategoryStore(state => state.loadCategories)

  useEffect(() => {
    if (!id) return

    // Handle both numeric IDs (local surveys) and UUID strings (remote surveys)
    // Check if it's a UUID format (contains hyphens) - if so, keep as string
    const isUUID = id.includes('-')
    const surveyId = isUUID ? id : parseInt(id, 10)

    loadSurvey(surveyId)
    loadCategories()

    return () => {
      clearCurrentSurvey()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Mobile layout
  if (isMobile && id) {
    const isUUID = id.includes('-')
    const surveyId = isUUID ? id : parseInt(id, 10)
    return <MobileSurveyPage surveyId={surveyId} />
  }

  const handleDownloadPhotos = async () => {
    setDownloadingPhotos(true)
    try {
      await downloadPhotosAsZip(currentSurvey, currentPhotos, categories)
    } catch (error) {
      console.error('Failed to download photos:', error)
      alert('Failed to download photos. Please try again.')
    } finally {
      setDownloadingPhotos(false)
    }
  }

  if (error || (!currentSurvey && !loading)) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-900 mb-2">Survey Not Found</h2>
          <p className="text-red-700 mb-4">{error || "The survey you're looking for doesn't exist."}</p>
          <p className="text-sm text-red-600 mb-4">
            This may be because the survey was deleted or synced to the cloud with a new ID.
            Please check the survey list for the current survey.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  if (loading || !currentSurvey) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col -m-4 lg:-m-6 p-4 lg:p-6">
      {/* Header */}
      <div className="flex-shrink-0 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              icon="ArrowLeft"
              onClick={() => navigate(`/survey/${id}`)}
            >
              Back to Survey
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {currentSurvey.propertyName}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                {currentPhotos.length} photos across {new Set(currentPhotos.map(p => p.categoryCode)).size} categories
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            icon="Download"
            onClick={handleDownloadPhotos}
            loading={downloadingPhotos}
            disabled={currentPhotos.length === 0}
          >
            {downloadingPhotos ? 'Downloading...' : 'Download All Photos'}
          </Button>
        </div>
      </div>

      {/* Photo Gallery */}
      <div className="flex-1 overflow-y-auto">
        <Card>
          <PhotoGallery
            photos={currentPhotos}
            onDeletePhoto={deletePhoto}
            onUpdatePhotoNotes={updatePhotoNotes}
            onTogglePhotoAnalyse={togglePhotoAnalyse}
            analysisResults={currentSurvey.analysisResult}
            groupByCategory={true}
          />
        </Card>
      </div>
    </div>
  )
}
